import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { apiGet } from '../services/api';
import { Trophy, ArrowLeft, CheckCircle2, XCircle, Star } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';

export default function AchievementDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [achievement, setAchievement] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchAchievement = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet(`/gamification/achievements/${id}`);
      
      if (response.success && response.achievement) {
        setAchievement(response.achievement);
      } else {
        setError('Achievement not found');
      }
    } catch (err: any) {
      console.error('Failed to fetch achievement:', err);
      setError(err.message || 'Failed to load achievement');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (id) {
      fetchAchievement();
    }
  }, [id, isAuthenticated, navigate, fetchAchievement]);

  if (loading) {
    return (
      <>
        <Header />
        <main style={{ 
          backgroundColor: '#1a1b20', 
          minHeight: '100vh',
          margin: 0,
          paddingTop: isMobile ? '220px' : '200px',
          paddingBottom: '60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--platinum)',
        }}>
          <p>Loading achievement...</p>
        </main>
        <Footer />
      </>
    );
  }

  if (error || !achievement) {
    return (
      <>
        <Header />
        <main style={{ 
          backgroundColor: '#1a1b20', 
          minHeight: '100vh',
          margin: 0,
          paddingTop: isMobile ? '220px' : '200px',
          paddingBottom: '60px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--platinum)',
        }}>
          <div className="container" style={{ maxWidth: '800px', margin: '0 auto', padding: '0 15px' }}>
          <XCircle size={48} color="var(--orange)" style={{ marginBottom: '20px' }} />
          <h2 style={{ color: 'var(--white)', marginBottom: '10px' }}>Achievement Not Found</h2>
          <p style={{ color: 'var(--platinum)', marginBottom: '20px' }}>{error || 'This achievement does not exist.'}</p>
          <Link
            to="/user/dashboard"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: 'var(--orange)',
              color: 'var(--white)',
              textDecoration: 'none',
              borderRadius: '4px',
              fontWeight: 'bold',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <ArrowLeft size={18} />
            Back to Dashboard
          </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{achievement.title} - Achievement | Oracle Gaming</title>
        <meta name="description" content={achievement.description || `View details about the ${achievement.title} achievement`} />
      </Helmet>
      <Header />
      <main style={{ 
        backgroundColor: '#1a1b20', 
        minHeight: '100vh',
        margin: 0,
        paddingTop: isMobile ? '220px' : '200px',
        paddingBottom: '60px'
      }}>
        <div className="container" style={{ maxWidth: '800px', margin: '0 auto', padding: '0 15px' }}>
          {/* Back Button */}
          <Link
            to="/user/dashboard"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--orange)',
              textDecoration: 'none',
              marginBottom: '30px',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            <ArrowLeft size={18} />
            Back to Dashboard
          </Link>

          {/* Achievement Card */}
          <div style={{
            background: 'var(--raisin-black-2)',
            border: achievement.earned ? '2px solid var(--orange)' : '2px solid rgba(20, 254, 251, 0.3)',
            borderRadius: '8px',
            padding: '40px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          }}>
            {/* Header Section */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '24px',
              marginBottom: '30px',
              flexWrap: 'wrap',
            }}>
              {/* Achievement Image */}
              <div style={{
                flexShrink: 0,
                width: '120px',
                height: '120px',
                borderRadius: '8px',
                background: achievement.earned ? 'rgba(20, 254, 251, 0.1)' : 'rgba(20, 254, 251, 0.05)',
                border: achievement.earned ? '2px solid var(--orange)' : '2px solid rgba(20, 254, 251, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}>
                {achievement.image ? (
                  <img
                    src={achievement.image}
                    alt={achievement.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Trophy 
                    size={60} 
                    color={achievement.earned ? "var(--orange)" : "var(--platinum)"}
                    style={{ opacity: achievement.earned ? 1 : 0.5 }}
                  />
                )}
              </div>

              {/* Title and Status */}
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '12px',
                  flexWrap: 'wrap',
                }}>
                  <h1 style={{
                    color: 'var(--white)',
                    fontSize: '28px',
                    fontWeight: 'bold',
                    margin: 0,
                  }}>
                    {achievement.title}
                  </h1>
                  {achievement.earned ? (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      background: 'rgba(20, 254, 251, 0.1)',
                      border: '1px solid var(--orange)',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: 'var(--orange)',
                    }}>
                      <CheckCircle2 size={16} />
                      Earned
                    </div>
                  ) : (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      background: 'rgba(20, 254, 251, 0.05)',
                      border: '1px solid rgba(20, 254, 251, 0.3)',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: 'var(--platinum)',
                      opacity: 0.7,
                    }}>
                      <XCircle size={16} />
                      Not Earned
                    </div>
                  )}
                </div>

                {/* Points */}
                {achievement.points > 0 && (
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    background: 'rgba(20, 254, 251, 0.1)',
                    borderRadius: '4px',
                    marginBottom: '12px',
                  }}>
                    <Star size={18} color="var(--orange)" />
                    <span style={{
                      color: 'var(--orange)',
                      fontSize: '16px',
                      fontWeight: 'bold',
                    }}>
                      +{achievement.points} XP
                    </span>
                  </div>
                )}

                {/* Earned Date */}
                {achievement.earned && achievement.earned_date && (
                  <p style={{
                    color: 'var(--platinum)',
                    fontSize: '14px',
                    margin: '8px 0 0 0',
                    opacity: 0.8,
                  }}>
                    Earned on {new Date(achievement.earned_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                )}
              </div>
            </div>

            {/* Description */}
            {achievement.description && (
              <div style={{
                marginBottom: '30px',
                paddingBottom: '30px',
                borderBottom: '1px solid rgba(20, 254, 251, 0.1)',
              }}>
                <h3 style={{
                  color: 'var(--white)',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  marginBottom: '12px',
                }}>
                  About This Achievement
                </h3>
                <p style={{
                  color: 'var(--platinum)',
                  fontSize: '16px',
                  lineHeight: '1.6',
                  margin: 0,
                }}>
                  {achievement.description}
                </p>
              </div>
            )}

            {/* How to Get Instructions */}
            {achievement.how_to_get && (
              <div style={{
                background: 'rgba(20, 254, 251, 0.03)',
                border: '1px solid rgba(20, 254, 251, 0.15)',
                borderRadius: '6px',
                padding: '16px 20px',
              }}>
                <h3 style={{
                  color: 'var(--orange)',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  {achievement.earned ? (
                    <>
                      <CheckCircle2 size={16} />
                      How You Earned This
                    </>
                  ) : (
                    <>
                      <Star size={16} />
                      How to Earn This Achievement
                    </>
                  )}
                </h3>
                <p style={{
                  color: 'var(--platinum)',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  margin: 0,
                  whiteSpace: 'pre-line',
                  opacity: 0.9,
                }}>
                  {achievement.how_to_get}
                </p>
              </div>
            )}

          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}

